<script lang="ts" setup>
import {
  IItemVariant,
  IItemVariantOption,
  IItemVariantOptionMap,
  PromotionType,
} from '@rediredi/types'
import { useTitle } from '@vueuse/core'
import { FALLBACK_IMAGE_PATH } from '~/consts/images'
import { SearchResponse } from '~/types/api'
import { IStorefrontItem } from '~/types/item'
import { clearHTMLTags } from '~/utils/global'

const { t } = useI18n()
const route = useRoute()
const quantityToAdd = ref(1)
const theme = useStoreTheme()
const isMobile = useIsMobile()
const itemStore = useItemStore()
const cartStore = useCartStore()
const wasItemJustAdded = ref(false)
const globalStore = useGlobalStore()
const product = ref<IStorefrontItem>()
const { event, metaEvent } = useEvent()
const optionsToAdd = ref<IItemVariantOptionMap[]>([])
const isNavigatorEnabled = typeof navigator.share === 'function'
const isVariationsEnabled = true
const isDiscountsEnabled = true
const currency = globalStore.storeCurrency

const getPageTitle = () => {
  return `${globalStore.store.name} - ${itemStore.currentItem?.baseVariant.title}`
}

const currentVariant = computed(() => {
  return (
    getVariantByOptions(itemStore.inStockVariants, optionsToAdd.value) ||
    itemStore?.currentItem?.baseVariant
  )
})

const pictures = computed(() => {
  if (itemStore.options.length) {
    if (currentVariant.value?.pictures?.length) return currentVariant.value.pictures
  }

  if (itemStore?.currentItem?.pictures?.length) return itemStore?.currentItem?.pictures

  return []
})

const filteredOptions = computed(() => {
  const secondary = itemStore.optionsInStock[1]
  const options: IItemVariantOption[] = [...itemStore.optionsInStock]

  if (!secondary) return options

  return options.map((option, i) => {
    if (i === 1) {
      return {
        ...option,
        values: option.values.filter((value) => {
          return !!getVariantByOptions(itemStore.inStockVariants, [
            { name: optionsToAdd.value[0].name, value: optionsToAdd.value[0].value },
            { name: secondary.name, value: value.name },
          ])
        }),
      }
    } else {
      return option
    }
  })
})

if (!itemStore.currentItem) {
  const qs = {
    perPage: '1',
    id: route.params.id as string,
  }
  const { data } = await useApiFetch<SearchResponse<IStorefrontItem>>(
    `/inventory/storefront/items?${new URLSearchParams(qs)}`,
  )
  const productApi = data.value?.data[0]

  if (productApi) {
    product.value = productApi
    itemStore.currentItem = productApi

    const images = getCoverVariantPictures(productApi)
    let imageUrl = FALLBACK_IMAGE_PATH

    if (images.length) imageUrl = optimizedImage(getPictureUrl(images[0]), 'small')

    useSeoMeta({
      title: getPageTitle(),
      ogTitle: productApi.baseVariant.title,
      description: clearHTMLTags(productApi.baseVariant.description ?? ''),
      ogDescription: clearHTMLTags(productApi.baseVariant.description ?? ''),
      ogImage: imageUrl,
      ogImageSecureUrl: imageUrl,
      ogType: 'website',
      ogImageWidth: 300,
      ogImageHeight: 200,
      twitterCard: 'summary_large_image',
    })
  }
}

const oldPrice = computed(() => {
  if (!itemStore.currentItem?.promotion?.showBeforeAndAfterPrice || !isDiscountsEnabled) return null

  return itemStore.currentItem?.baseVariant.price
})
const price = computed(() => {
  const price =
    itemStore.currentItem?.promotion &&
    itemStore.currentItem?.promotion.type !== PromotionType.Label &&
    isDiscountsEnabled
      ? itemStore.currentItem?.baseVariant.promotionPrice
      : itemStore.currentItem?.baseVariant.price
  return price
})

const redirect = () => {
  useItemRedirect(itemStore.currentItem)
}

const share = () => {
  const text = t('components.header.share.products.view', {
    price: price.value?.amount
      ? formatPrice(price.value.amount, {
          currency: globalStore.storeCurrency || price.value.currency,
        })
      : '-',
  })

  navigator.share({
    url: window.location.href,
    text,
  })

  event('share', {
    content_type: 'link',
    item_id: itemStore.currentItem?.id,
  })
}

const getLabel = (label: 'category' | 'unity' | 'condition' | 'identifierValue' | 'brand') => {
  const notAvailable = t('globals.notAvailable')
  if (label === 'category') {
    const category = itemStore?.currentItem?.category?.name
    return category || notAvailable
  }

  if (label === 'unity') {
    const unit = itemStore?.currentItem?.unity
    return unit ? t(`globals.itemUnits.${unit}`) : notAvailable
  }

  if (label === 'condition') {
    const condition = itemStore?.currentItem?.condition
    return condition ? t(`globals.itemCondition.${condition}`) : notAvailable
  }

  if (label === 'identifierValue') {
    const identifierValue = itemStore?.currentItem?.baseVariant?.identifierValue
    return identifierValue ?? notAvailable
  }

  if (label === 'brand') {
    const brand = itemStore?.currentItem?.brand

    return brand ?? t('globals.noBrand')
  }
}

const handleAddToCart = () => {
  if (wasItemJustAdded.value) return

  cartStore.addItem(
    toCartItem(
      itemStore.currentItem as IStorefrontItem,
      quantityToAdd.value,
      currentVariant.value as IItemVariant,
      globalStore.storeCurrency,
    ),
    quantityToAdd.value,
  )
  wasItemJustAdded.value = true

  setTimeout(() => {
    wasItemJustAdded.value = false
    quantityToAdd.value = 1
  }, 3000)
}

const cartItem = computed(() =>
  cartStore.cart.items.find(
    (item) => item.id === itemStore?.currentItem?.id && item.variantId === currentVariant.value?.id,
  ),
)

const maxQuantity = computed(() => {
  return (currentVariant.value?.quantityAvailable || 0) - (cartItem?.value?.quantity || 0)
})

const isAddButtonDisabled = computed(
  () => quantityToAdd.value > maxQuantity.value || !quantityToAdd.value,
)

const isOutOfStock = computed(() => (itemStore.currentItem?.quantityAvailable || 0) < 1)

onMounted(() => {
  if (itemStore.currentItem) {
    event('view_item', {
      currency: globalStore.storeCurrency || itemStore.currentItem.baseVariant.price?.currency,
      value: itemStore.currentItem.baseVariant.price?.amount,
      items: [itemToAnalytics(itemStore.currentItem)],
    })
  }

  useTitle(getPageTitle())

  if (!optionsToAdd.value.length) {
    optionsToAdd.value = []
    itemStore.inStockVariants.forEach((variant) => {
      if (variant.position === 1 && variant.optionMapping) {
        optionsToAdd.value.push(...variant.optionMapping)
      }
    })

    if (!optionsToAdd.value.length && itemStore.inStockVariants[0]?.optionMapping)
      optionsToAdd.value.push(...itemStore.inStockVariants[0].optionMapping)
  }
})

definePageMeta({
  nested: true,
  showCart: true,
  shareable: true,
  context: 'products.view',
  noLinks: true,
})

onUnmounted(() => {
  itemStore.currentItem = undefined
})

if (process.client) {
  metaEvent('ViewContent', MetaEventsPayloads.viewContent(itemStore.currentItem as IStorefrontItem))
}
</script>

<template>
  <div class="m-auto mt-12 max-w-[1280px] sm:mt-20">
    <span itemprop="image" itemscope itemtype="image/webp">
      <link itemprop="url" :href="optimizedImage(getPictureUrl(pictures?.[0]), 'small')" />
    </span>
    <div class="product-details flex flex-col gap-4 px-4 py-6 md:flex-row md:gap-6 lg:gap-16">
      <ClientOnly>
        <h2 v-if="isMobile" class="mt-4 text-lg font-semibold text-gray-400">
          {{ itemStore?.currentItem?.baseVariant?.title }}
        </h2>

        <RProductImages
          :promotion="itemStore.currentItem?.promotion"
          :is-out-of-stock="isOutOfStock"
          :pictures="pictures"
        />

        <div v-if="!isMobile" class="flex-1">
          <h2 class="text-xl font-semibold text-gray-400 md:text-2xl">
            {{ itemStore?.currentItem?.baseVariant?.title }}
          </h2>

          <div class="flex gap-6 pt-8 text-sm font-medium text-gray-400">
            <div class="flex min-w-[250px] flex-col gap-4">
              <div class="flex flex-col gap-2">
                <div>{{ $t('pages.pdp.brand') }}</div>
                <div class="font-bold">
                  {{ getLabel('brand') }}
                </div>
              </div>

              <div class="flex flex-col gap-2">
                <div>{{ $t('pages.pdp.category') }}</div>
                <div class="font-bold">
                  <div>{{ getLabel('category') }}</div>
                </div>
              </div>
            </div>

            <div class="flex flex-col gap-4">
              <div class="flex flex-col gap-2">
                <div>{{ $t('pages.pdp.unit') }}</div>
                <div class="font-bold">
                  {{ getLabel('unity') }}
                </div>
              </div>

              <div class="flex flex-col gap-2">
                <div>{{ $t('pages.pdp.condition') }}</div>
                <div class="font-bold">
                  {{ getLabel('condition') }}
                </div>
              </div>
            </div>
          </div>

          <div class="mt-4 flex flex-col gap-2 text-sm font-medium text-gray-400">
            <div>{{ $t('pages.pdp.barcode') }}</div>

            <div class="font-bold">
              {{ getLabel('identifierValue') }}
            </div>
          </div>
          <div v-if="itemStore.options.length && isVariationsEnabled && !isOutOfStock" class="mt-8">
            <RProductOptions v-model:selected-options="optionsToAdd" :options="filteredOptions" />
          </div>

          <div class="mb-8 mt-16 inline-flex items-end gap-1 text-gray-400">
            <div v-if="price?.amount" class="text-left">
              <template v-if="oldPrice">
                <div class="text-2xl font-medium text-gray-400 line-through">
                  <span>{{ formatPriceInParts(oldPrice.amount, { currency }).currency }}</span>
                  <span>{{ formatPriceInParts(oldPrice.amount, { currency }).amount }}</span>
                </div>
              </template>

              <span class="text-3xl font-bold">
                {{ formatPriceInParts(price.amount, { currency }).currency }}
              </span>
              <span class="text-3xl font-bold">
                {{ formatPriceInParts(price.amount, { currency }).amount }}
              </span>
            </div>
            <template v-else>
              <div>
                <div class="text-sm text-gray-400">
                  {{ $t('pages.pdp.price') }}
                </div>
                <div class="text-3xl font-bold">
                  {{ $t('globals.noPrice') }}
                </div>
              </div>
            </template>
          </div>

          <div>
            <template v-if="globalStore.isCheckoutEnabled">
              <div class="flex flex-col gap-8">
                <div v-if="!isOutOfStock" class="flex flex-col items-start gap-4">
                  <div class="flex items-center gap-6">
                    <RBaseQuantityInput
                      class="h-12 min-w-[160px]"
                      size="small"
                      :quantity="quantityToAdd"
                      :max-quantity="maxQuantity"
                      @increment="quantityToAdd++"
                      @decrement="quantityToAdd--"
                      @update:quantity="quantityToAdd = $event"
                    />
                    <RBaseButton
                      v-if="!isOutOfStock"
                      :color="wasItemJustAdded ? 'success' : 'primary'"
                      class="h-12 w-full px-6 text-sm font-medium uppercase sm:mx-0"
                      :disabled="isAddButtonDisabled"
                      @click="handleAddToCart"
                    >
                      <RIcon v-if="wasItemJustAdded" size="20" name="ic:round-check" />
                      <RIcon v-else size="20">
                        <IconBag />
                      </RIcon>
                      {{
                        wasItemJustAdded
                          ? t('components.addToCartButton.added', quantityToAdd)
                          : t('pages.pdp.addToCart')
                      }}
                    </RBaseButton>
                  </div>

                  <div class="text-sm font-normal text-gray-400">
                    {{
                      t('pages.pdp.availableStock', {
                        stock: maxQuantity,
                      })
                    }}
                  </div>
                </div>

                <div
                  class="flex max-w-full flex-col items-center justify-center gap-1 md:max-w-[400px]"
                >
                  <RBaseButton
                    class="w-full border-1 border-whatsapp bg-white px-4 py-3 text-sm font-semibold uppercase text-whatsapp"
                    color="none"
                    :full-width="false"
                    @click="redirect"
                  >
                    <RIcon name="mdi:whatsapp" size="24" />
                    {{
                      !isOutOfStock
                        ? t('pages.pdp.whatsappButtonLabel')
                        : t('pages.pdp.whatsappButtonLabelNoStock')
                    }}
                  </RBaseButton>
                </div>
              </div>
            </template>

            <div v-else class="flex w-full flex-col gap-6 lg:flex-row">
              <RBaseButton
                v-if="isOutOfStock"
                class="max-w-[400px] border-1 border-whatsapp bg-white px-4 py-3 text-sm font-semibold uppercase text-whatsapp"
                color="none"
                :full-width="false"
                @click="redirect"
              >
                <RIcon name="mdi:whatsapp" size="24" />
                {{ t('pages.pdp.whatsappButtonLabelNoStock') }}
              </RBaseButton>
              <button
                v-else
                class="flex w-full flex-1 items-center justify-center rounded-full bg-whatsapp px-6 py-4 uppercase"
                :style="{
                  color: theme.text,
                }"
                @click="redirect"
              >
                <div class="flex items-center justify-center gap-2">
                  {{ $t('components.buyButton.label') }}
                  <RIcon size="22" name="mdi:whatsapp"></RIcon>
                </div>
              </button>

              <div class="w-full flex-1">
                <RShareButton v-if="isNavigatorEnabled" @share="share" />
                <div v-else>&nbsp;</div>
              </div>
            </div>
          </div>

          <div
            v-if="itemStore?.currentItem?.baseVariant?.description"
            class="mt-16 flex flex-col gap-8"
          >
            <div class="text-xl font-semibold text-gray-400">
              {{ $t('pages.pdp.productDescription') }}
            </div>

            <div>
              <p
                class="product-description text-sm font-medium text-gray-400"
                v-html="stringToHTML(itemStore?.currentItem?.baseVariant?.description ?? '')"
              ></p>
            </div>
          </div>
        </div>

        <div v-else class="flex-1">
          <template v-if="itemStore.options.length && isVariationsEnabled && !isOutOfStock">
            <hr class="relative -left-4 mb-5 w-screen border-[1.5px] border-mercury" />

            <RProductOptions v-model:selected-options="optionsToAdd" :options="filteredOptions" />

            <hr class="relative -left-4 my-5 w-screen border-[1.5px] border-mercury" />
          </template>

          <div>
            <template v-if="globalStore.isCheckoutEnabled">
              <div class="flex flex-col gap-6">
                <div>
                  <div class="mb-2 flex items-center justify-between gap-3">
                    <div class="inline-flex items-end gap-1 text-gray-400">
                      <div v-if="price?.amount" class="text-left">
                        <template v-if="oldPrice">
                          <div class="text-1xl font-medium text-gray-400 line-through">
                            <span>
                              {{ formatPriceInParts(oldPrice.amount, { currency }).currency }}
                            </span>
                            <span>
                              {{ formatPriceInParts(oldPrice.amount, { currency }).amount }}
                            </span>
                          </div>
                        </template>

                        <span class="text-2xl font-bold">
                          {{ formatPriceInParts(price.amount, { currency }).currency }}
                        </span>
                        <span class="text-2xl font-bold">
                          {{ formatPriceInParts(price.amount, { currency }).amount }}
                        </span>
                      </div>

                      <template v-else>
                        <div>
                          <div class="text-sm text-gray-400">
                            {{ $t('pages.pdp.price') }}
                          </div>
                          <div class="text-l font-bold">
                            {{ $t('globals.noPrice') }}
                          </div>
                        </div>
                      </template>
                    </div>

                    <div v-if="!isOutOfStock" class="flex flex-col items-end gap-2">
                      <RBaseQuantityInput
                        class="h-12 min-w-[160px]"
                        size="small"
                        :quantity="quantityToAdd"
                        :max-quantity="maxQuantity"
                        @increment="quantityToAdd++"
                        @decrement="quantityToAdd--"
                        @update:quantity="quantityToAdd = $event"
                      />
                    </div>
                  </div>
                  <div v-if="!isOutOfStock" class="text-right text-sm font-normal text-gray-400">
                    {{
                      t('pages.pdp.availableStock', {
                        stock: maxQuantity,
                      })
                    }}
                  </div>
                </div>

                <div class="flex flex-col items-center justify-center gap-4">
                  <RBaseButton
                    v-if="(itemStore.currentItem?.quantityAvailable || 0) > 0"
                    :color="wasItemJustAdded ? 'success' : 'primary'"
                    class="h-12 w-full px-6 text-sm font-medium uppercase transition-all sm:mx-0"
                    :disabled="isAddButtonDisabled"
                    @click="handleAddToCart"
                  >
                    <RIcon v-if="wasItemJustAdded" size="20" name="ic:round-check" />
                    <RIcon v-else size="20">
                      <IconBag />
                    </RIcon>
                    {{
                      wasItemJustAdded
                        ? t('components.addToCartButton.added', quantityToAdd)
                        : t('pages.pdp.addToCart')
                    }}
                  </RBaseButton>

                  <RBaseButton
                    class="border-1 border-whatsapp bg-white px-4 py-3 text-sm font-semibold uppercase text-whatsapp"
                    color="none"
                    @click="redirect"
                  >
                    <RIcon name="mdi:whatsapp" size="24" />
                    {{
                      (itemStore.currentItem?.quantityAvailable || 0) > 0
                        ? t('pages.pdp.whatsappButtonLabel')
                        : t('pages.pdp.whatsappButtonLabelNoStock')
                    }}
                  </RBaseButton>
                </div>
              </div>
            </template>

            <div v-else class="flex w-full flex-col gap-6 lg:flex-row">
              <div class="inline-flex items-end gap-1 text-gray-400">
                <div v-if="price?.amount" class="text-left">
                  <template v-if="oldPrice">
                    <div class="text-1xl font-medium text-gray-400 line-through">
                      <span>{{ formatPriceInParts(oldPrice.amount, { currency }).currency }}</span>
                      <span>{{ formatPriceInParts(oldPrice.amount, { currency }).amount }}</span>
                    </div>
                  </template>

                  <span class="text-2xl font-bold">
                    {{ formatPriceInParts(price.amount, { currency }).currency }}
                  </span>
                  <span class="text-2xl font-bold">
                    {{ formatPriceInParts(price.amount, { currency }).amount }}
                  </span>
                </div>

                <template v-else>
                  <div>
                    <div class="text-sm text-gray-400">
                      {{ $t('pages.pdp.price') }}
                    </div>
                    <div class="text-l font-bold">
                      {{ $t('globals.noPrice') }}
                    </div>
                  </div>
                </template>
              </div>
              <RBaseButton
                v-if="isOutOfStock"
                class="max-w-[400px] border-1 border-whatsapp bg-white px-4 py-3 text-sm font-semibold uppercase text-whatsapp"
                color="none"
                :full-width="false"
                @click="redirect"
              >
                <RIcon name="mdi:whatsapp" size="24" />
                {{ t('pages.pdp.whatsappButtonLabelNoStock') }}
              </RBaseButton>
              <button
                v-else
                class="flex w-full flex-1 items-center justify-center rounded-full bg-whatsapp px-6 py-4 uppercase"
                :style="{
                  color: theme.text,
                }"
                @click="redirect"
              >
                <div class="flex items-center justify-center gap-2">
                  {{ $t('components.buyButton.label') }}
                  <RIcon size="22" name="mdi:whatsapp"></RIcon>
                </div>
              </button>

              <div class="w-full flex-1">
                <RShareButton v-if="isNavigatorEnabled" @share="share" />
                <div v-else>&nbsp;</div>
              </div>
            </div>
          </div>

          <hr class="relative -left-4 my-5 w-screen border-[1.5px] border-mercury" />

          <div class="flex flex-col gap-2 text-sm font-medium text-gray-400">
            <div class="grid grid-cols-2 gap-1">
              <div>{{ $t('pages.pdp.brand') }}</div>
              <div class="font-bold">
                {{ getLabel('brand') }}
              </div>
            </div>

            <div class="grid grid-cols-2 gap-1">
              <div>{{ $t('pages.pdp.category') }}</div>
              <div class="font-bold">
                <div>{{ getLabel('category') }}</div>
              </div>
            </div>

            <div class="grid grid-cols-2 gap-1">
              <div>{{ $t('pages.pdp.barcode') }}</div>

              <div class="font-bold">
                {{ getLabel('identifierValue') }}
              </div>
            </div>

            <div class="grid grid-cols-2 gap-1">
              <div>{{ $t('pages.pdp.unit') }}</div>
              <div class="font-bold">
                {{ getLabel('unity') }}
              </div>
            </div>

            <div class="grid grid-cols-2 gap-1">
              <div>{{ $t('pages.pdp.condition') }}</div>
              <div class="font-bold">
                {{ getLabel('condition') }}
              </div>
            </div>
          </div>

          <hr
            v-if="itemStore?.currentItem?.baseVariant?.description"
            class="relative -left-4 my-5 w-screen border-[1.5px] border-mercury"
          />

          <div v-if="itemStore?.currentItem?.baseVariant?.description" class="flex flex-col gap-4">
            <div class="text-base font-semibold text-gray-400">
              {{ $t('pages.pdp.productDescription') }}
            </div>

            <div>
              <p
                class="product-description text-sm font-medium text-gray-400"
                v-html="stringToHTML(itemStore?.currentItem?.baseVariant?.description ?? '')"
              ></p>
            </div>
          </div>
        </div>
      </ClientOnly>
    </div>
  </div>
</template>

<style scoped>
.product-description {
  :deep(pre) {
    text-wrap: wrap;
  }

  :deep(p) {
    margin-top: 1em;
  }

  :deep(ul) {
    margin-left: 1em;
    li {
      list-style-type: disc;
      p {
        margin-top: 0;
      }
    }
  }

  :deep(ol) {
    margin-left: 1em;
    li {
      list-style-type: decimal;
      p {
        margin-top: 0;
      }
    }
  }
}
</style>
